import Box, { type BoxProps } from '@mui/material/Box';
import Container from '@mui/material/Container';

import { SectionBackdrop, type SectionBackdropProps, SectionPattern } from '@/components/atoms';
import { type SectionPatternName } from '@/theme';

export type SectionTone = 'default' | 'muted' | 'inverse';

type Props = BoxProps & {
  tone?: SectionTone;
  labelledBy?: string;
  /**
   * An optional decorative texture behind the band. Tone is the band's
   * colour and pattern is its surface; they are separate axes on purpose, so
   * any tone can wear any pattern. Omitted, the band is flat and nothing
   * extra is rendered — which is what the other pages still get.
   */
  pattern?: SectionPatternName;
  /**
   * An optional photographic layer behind the band: a car cut-out anchored to
   * a bottom corner, faded to a watermark. A third axis beside tone and
   * pattern, so a band can wear any combination of the three — and, like the
   * other two, it changes nothing for a band that does not ask for one.
   */
  backdrop?: SectionBackdropProps;
};

/** One page band: consistent vertical rhythm, width and background. */
export function Section({
  tone = 'default',
  labelledBy,
  pattern,
  backdrop,
  children,
  sx,
  ...rest
}: Props) {
  // Either decorative layer is `inset: 0` against the band and stacked under
  // the content, so either one makes the band a containing block. A band with
  // neither keeps the exact box it had.
  const layered = !!pattern || !!backdrop;

  return (
    <Box
      component="section"
      aria-labelledby={labelledBy}
      sx={[
        // 56px on a phone was the desktop rhythm scaled down rather than
        // reasoned about, and it was paid twice: once above the band's
        // heading and once below the one before it, so two bands stood 112px
        // apart on a 390px screen. It also set the distance between the
        // sticky header and the first heading on every page, which since the
        // opening band stopped being drawn on phones is the first thing a
        // visitor sees — 56px of empty surface under the nav.
        //
        // 32px separates two bands at 64px, which is still clearly a gap, and
        // puts the first heading where a catalogue app puts it.
        { py: { xs: 8, md: 24 } },
        // Only a patterned band becomes a containing block, and only so the
        // texture has something to be `inset: 0` against. No `overflow:
        // hidden` with it: the layer cannot escape a box it is inset into
        // anyway, and clipping here would cut the bleed off the testimonial
        // row, which is deliberately wider than its container. Flat bands
        // keep the exact box they had, so nothing on the other pages moves.
        layered && { position: 'relative' },
        tone === 'muted' && { bgcolor: 'surfaceVariant' },
        tone === 'inverse' &&
          ((theme) => ({
            bgcolor: '#0B0B0C',
            color: '#FFFFFF',
            ...theme.applyStyles('dark', { bgcolor: '#1F1A1B' }),
          })),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      {...rest}
    >
      {pattern && <SectionPattern name={pattern} />}
      {backdrop && <SectionBackdrop {...backdrop} onDarkBand={tone === 'inverse'} />}
      <Container maxWidth="lg" sx={[layered && { position: 'relative', zIndex: 1 }]}>
        {children}
      </Container>
    </Box>
  );
}
